function cleanTargetValue(value) {
  return value?.toString() ?? ''
}

const ANNUAL_ROCE_BENCHMARK = 0.2
const JV_EFFECTIVENESS_MULTIPLIER = 0.5

export function buildTargetBridge(values) {
  const targetMonthlyCashflow = numericTargetValue(values?.targetMonthlyCashflowGross)
  const availableCapital = numericTargetValue(values?.availableCapital)
  const jvCapital = numericTargetValue(values?.jvCapitalAccess)
  const timeframeYears = numericTargetValue(values?.timeframeYears)
  const effectiveCapital = availableCapital + (jvCapital * JV_EFFECTIVENESS_MULTIPLIER)
  const requiredAnnualIncome = targetMonthlyCashflow * 12
  const requiredReturn = effectiveCapital > 0 ? requiredAnnualIncome / effectiveCapital : null
  const expectedTimeframeReturn = timeframeYears * ANNUAL_ROCE_BENCHMARK
  const targetIntensityRatio = requiredReturn === null || expectedTimeframeReturn <= 0
    ? null
    : requiredReturn / expectedTimeframeReturn
  const feasibilityBand = feasibilityBandFor(targetIntensityRatio)
  const insights = insightsFor({ feasibilityBand, availableCapital, jvCapital }).slice(0, 3)

  return {
    values,
    feasibilityBand,
    insights,
    targetSummary: targetSummaryFor(values),
  }
}

export function numericTargetValue(value) {
  const number = Number.parseInt(cleanTargetValue(value).replace(/\D/g, ''), 10)
  return Number.isNaN(number) ? 0 : number
}

export function formatTargetCurrency(value) {
  const amount = numericTargetValue(value)
  return `£${amount.toLocaleString('en-GB')}`
}

function feasibilityBandFor(targetIntensityRatio) {
  if (targetIntensityRatio === null) return 'extreme'
  if (targetIntensityRatio < 0.7) return 'comfortable'
  if (targetIntensityRatio <= 1) return 'achievable'
  if (targetIntensityRatio <= 1.5) return 'stretch'
  if (targetIntensityRatio <= 2) return 'aggressive'

  return 'extreme'
}

function insightsFor({ feasibilityBand, availableCapital, jvCapital }) {
  const insights = [primaryInsightFor({ feasibilityBand, availableCapital, jvCapital })]

  if (jvCapital > availableCapital) {
    insights.push({
      title: 'Your plan depends meaningfully on investor capital',
      what: 'A meaningful part of your growth plan relies on successfully raising external finance.',
      why: 'Some strategies are materially easier to fund than others.',
      unlock: 'Step 2 may prioritise strategies that lenders and investors are more likely to back.',
    })
  }

  return insights
}

function primaryInsightFor({ feasibilityBand, availableCapital, jvCapital }) {
  if (feasibilityBand === 'comfortable') {
    return {
      title: 'Your target appears comfortably achievable',
      what: 'Relative to your capital and timeframe, your goal appears conservative.',
      why: 'You may be able to prioritise lower complexity and greater passivity.',
      unlock: 'Sentinel will help identify strategies aligned with your preferred involvement level.',
    }
  }

  if (feasibilityBand === 'achievable') {
    return {
      title: 'Your target looks achievable with disciplined execution',
      what: 'Relative to your capital and timeframe, this target appears realistic with the right strategy.',
      why: 'Traditional low-yield approaches are unlikely to get there.',
      unlock: 'Sentinel will prioritise strategies with stronger income generation and value creation potential.',
    }
  }

  if (feasibilityBand === 'stretch') {
    return {
      title: 'Your target is a stretch and needs sharper execution',
      what: 'Relative to your capital and timeframe, this goal requires a more deliberate strategy.',
      why: 'Lower-yield or slow-moving approaches may not create enough momentum.',
      unlock: 'Sentinel will prioritise strategies with stronger execution discipline and clearer value creation.',
    }
  }

  if (feasibilityBand === 'aggressive') {
    return {
      title: 'Your target is aggressive and likely needs leverage',
      what: 'This target is materially above a standard execution path for your current resources.',
      why: 'You may need leverage, external capital, or faster value creation to make the plan credible.',
      unlock: [
        'Creative finance',
        'Joint venture capital',
        'Accelerated value creation',
      ],
    }
  }

  if (availableCapital + jvCapital === 0) {
    return {
      title: 'Your target depends on creating capital access',
      what: 'The target can still be pursued, but the next step needs to account for limited deployable capital.',
      why: 'Funding route, partner fit, and lender appetite become central constraints.',
      unlock: 'Sentinel will frame strategies around financeability and execution path.',
    }
  }

  return {
    title: 'Your target is extreme without significant intervention',
    what: 'Relative to your capital and timeframe, this goal is unlikely through normal execution alone.',
    why: 'The plan likely needs a major change in capital access, timeframe, deal structure, or execution intensity.',
    unlock: [
      'Creative finance',
      'Joint venture capital',
      'Accelerated value creation',
      'Higher-return strategies',
    ],
  }
}

function targetSummaryFor(values) {
  const timeframeYears = numericTargetValue(values.timeframeYears)

  return [
    {
      label: 'Monthly Target',
      value: `${formatTargetCurrency(values.targetMonthlyCashflowGross)} gross`,
    },
    {
      label: 'Timeframe',
      value: `${timeframeYears} ${timeframeYears === 1 ? 'year' : 'years'}`,
    },
    { label: 'Capital', value: formatTargetCurrency(values.availableCapital) },
    { label: 'JV Access', value: formatTargetCurrency(values.jvCapitalAccess) },
  ]
}
